import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';

import useDebounce from '@/client/hooks/useDebounce';
import { useClientStore } from '@/client/stores/client';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * An id field that also takes a name. Staff rarely know a uid, oid, lid or eid
 * by heart, so typing letters searches by name and picking a result fills the
 * id; typing digits still sets the id directly, and its name is looked up so
 * the choice can be checked before saving.
 */
export interface LookupHit {
  id: number;
  label: string;
  detail?: string;
}

type Request = <T = unknown>(endpoint: string) => Promise<T>;

interface LookupSource {
  noun: string;
  search: (request: Request, q: string) => Promise<LookupHit[]>;
  /** The name for an id typed directly; `null` when there is no such record. */
  resolve: (request: Request, id: number) => Promise<string | null>;
}

/** The proxy wraps payloads as `{ status, data }`. */
const payload = (raw: any) => raw?.data ?? raw;
const enc = encodeURIComponent;

const userHits = (raw: any): LookupHit[] =>
  (payload(raw)?.users ?? []).map((u: any) => ({
    id: Number(u.uid),
    label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.uid}`,
    detail: u.email,
  }));

const SOURCES = {
  user: {
    noun: 'user',
    search: async (request, q) => userHits(await request(`/api/admin/users/search?search=${enc(q)}`)),
    resolve: async (request, id) => {
      const u = payload(await request(`/api/admin/users/${id}`))?.user;
      return u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.preferred_name || u.email || null : null;
    },
  },
  org: {
    noun: 'organization',
    search: async (request, q) =>
      (payload(await request(`/api/admin/organizations/search?search=${enc(q)}&limit=8`))?.organizations ?? []).map(
        (o: any) => ({ id: Number(o.oid), label: o.name || `Org #${o.oid}`, detail: o.ffid ?? undefined }),
      ),
    resolve: async (request, id) => payload(await request(`/api/admin/organizations/lookup?oid=${id}`))?.name ?? null,
  },
  location: {
    noun: 'location',
    search: async (request, q) => {
      const rows = payload(await request(`/api/v2/location/autocomplete?query=${enc(q)}&limit=8`));
      return (Array.isArray(rows) ? rows : []).map((l: any) => ({ id: Number(l.lid), label: l.name || `Location #${l.lid}` }));
    },
    resolve: async (request, id) => {
      const data = payload(await request(`/api/v2/location/${id}`));
      return data?.name ?? data?.location?.name ?? null;
    },
  },
  listing: {
    noun: 'listing',
    search: async (request, q) =>
      (payload(await request(`/api/admin/entities/search?q=${enc(q)}&page_size=8`))?.entities ?? []).map((r: any) => {
        const e = r.Entity ?? r;
        return { id: Number(e.eid), label: e.name || e.name_basic || `#${e.eid}`, detail: e.ffid ?? undefined };
      }),
    resolve: async (request, id) => {
      const data = payload(await request(`/api/v2/entity/${id}`));
      const e = data?.Entity ?? data;
      return e?.name || e?.name_basic || null;
    },
  },
} satisfies Record<string, LookupSource>;

export type LookupKind = keyof typeof SOURCES;

export const IdLookup: React.FC<{
  kind: LookupKind;
  /** The id, as the digits-only string the forms already keep. */
  value: string;
  onChange: (id: string) => void;
  /** Called with the full hit when a search result is picked. */
  onPick?: (hit: LookupHit) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}> = ({ kind, value, onChange, onPick, disabled, placeholder, className, id }) => {
  const source: LookupSource = SOURCES[kind];
  const request = useClientStore((s) => s.request) as Request;
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<LookupHit | null>(null);

  const q = useDebounce(text.trim(), 300);
  const searching = q.length >= 2 && !/^\d+$/.test(q);

  const hits = useQuery({
    queryKey: ['id-lookup', kind, 'search', q],
    enabled: searching,
    queryFn: () => source.search(request, q),
    retry: false,
  });

  const valueId = value ? Number(value) : null;
  const known = picked && picked.id === valueId ? picked.label : undefined;
  const resolved = useQuery({
    queryKey: ['id-lookup', kind, 'resolve', valueId],
    enabled: valueId != null && !known,
    queryFn: async () => {
      try {
        return await source.resolve(request, valueId!);
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
  const name = known ?? resolved.data;

  const onText = (v: string) => {
    setText(v);
    setOpen(true);
    const t = v.trim();
    // Digits are an id typed directly; letters change nothing until a pick.
    if (/^\d*$/.test(t)) onChange(t);
  };

  const pick = (hit: LookupHit) => {
    setPicked(hit);
    setText('');
    setOpen(false);
    onChange(String(hit.id));
    onPick?.(hit);
  };

  const rows = (hits.data ?? []).filter((h) => Number.isFinite(h.id));

  return (
    <div
      className={cn('relative', className)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
    >
      <Input
        id={id}
        value={text}
        disabled={disabled}
        onChange={(e) => onText(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? `Search ${source.noun} by name, or type an id`}
        autoComplete="off"
      />
      {value && (
        <p className="mt-1 flex items-center gap-1 text-xs text-gray-600">
          <span className="min-w-0 truncate">
            {name ? <span className="font-medium text-gray-900">{name}</span> : null}
            {resolved.isFetching ? 'Looking up… ' : !name && resolved.isFetched ? `No ${source.noun} found — ` : name ? ' · ' : ''}
            #{value}
          </span>
          {!disabled && (
            <button
              type="button"
              aria-label={`Clear ${source.noun}`}
              onClick={() => { setText(''); setPicked(null); onChange(''); }}
              className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </p>
      )}
      {open && searching && (
        <ul className="absolute left-0 top-10 z-20 max-h-56 w-full min-w-[16rem] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg">
          {rows.map((hit) => (
            <li key={hit.id}>
              <button
                type="button"
                onClick={() => pick(hit)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-gray-900">{hit.label}</span>
                  {hit.detail && <span className="block truncate text-xs text-gray-400">{hit.detail}</span>}
                </span>
                <span className="shrink-0 font-mono text-xs text-gray-400">#{hit.id}</span>
              </button>
            </li>
          ))}
          {rows.length === 0 && (
            <li className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
              {hits.isFetching ? <><Loader2 className="h-3 w-3 animate-spin" /> Searching…</> : hits.isError ? 'Search failed — type the id instead.' : `No ${source.noun} found.`}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
